import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class UnknownTag extends BaseObj {
    public AlphaTag: string | null;
    public __alphatag: string | null;

    public DeviceId: string;
    public __deviceid: string;

    public EventID: number;
    public __eventid: number;

    public ShootingResult: string | null;
    public __shootingresult: string | null;

    public StationId: number;
    public __stationid: number;

    public Tag: number | null;
    public __tag: number | null;

    public Time: Date;
    public __time: Date;

    public TUID: string;
    public __tuid: string;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AlphaTag?: string | null;
        DeviceId?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        ShootingResult?: string | null;
        StationId?: number;
        Tag?: number | null;
        Time?: Date;
        TUID?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<UnknownTag>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('UnknownTag tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.AlphaTag = values.AlphaTag ?? null;
        this.__alphatag = values.AlphaTag ?? null;
        Object.defineProperty(this, 'AlphaTag', {
            get(): string | null {
                return this.__alphatag;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('AlphaTag', this.__alphatag, value);
                this.__alphatag = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DeviceId = values.DeviceId ?? '';
        this.__deviceid = values.DeviceId ?? '';
        Object.defineProperty(this, 'DeviceId', {
            get(): string {
                return this.__deviceid;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('DeviceId', this.__deviceid, value);
                this.__deviceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShootingResult = values.ShootingResult ?? null;
        this.__shootingresult = values.ShootingResult ?? null;
        Object.defineProperty(this, 'ShootingResult', {
            get(): string | null {
                return this.__shootingresult;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ShootingResult', this.__shootingresult, value);
                this.__shootingresult = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationId = values.StationId ?? 0;
        this.__stationid = values.StationId ?? 0;
        Object.defineProperty(this, 'StationId', {
            get(): number {
                return this.__stationid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StationId', this.__stationid, value);
                this.__stationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Tag = values.Tag ?? null;
        this.__tag = values.Tag ?? null;
        Object.defineProperty(this, 'Tag', {
            get(): number | null {
                return this.__tag;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Tag', this.__tag, value);
                this.__tag = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        this.__time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Time', {
            get(): Date {
                return this.__time;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Time', this.__time, value);
                this.__time = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TUID = values.TUID ?? '';
        this.__tuid = values.TUID ?? '';
        Object.defineProperty(this, 'TUID', {
            get(): string {
                return this.__tuid;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('TUID', this.__tuid, value);
                this.__tuid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"AlphaTag": ${JSON.stringify(this.AlphaTag)}` +
            ',' +
            `"DeviceId": ${JSON.stringify(this.DeviceId)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"ShootingResult": ${JSON.stringify(this.ShootingResult)}` +
            ',' +
            `"StationId": ${JSON.stringify(this.StationId)}` +
            ',' +
            `"Tag": ${JSON.stringify(this.Tag)}` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of UnknownTag from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AlphaTag?: string | null;
        DeviceId?: string | null;
        EventID?: number;
        ShootingResult?: string | null;
        StationId?: number;
        Tag?: number | null;
        Time?: Date;
        TUID?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): UnknownTag {
        return new UnknownTag(values);
    }

    public static _FromServerJson(json: any): UnknownTag {
        return new UnknownTag({
            AlphaTag: json.AlphaTag ?? '',
            DeviceId: json.DeviceId ?? '',
            EventID: json.EventID,
            ShootingResult: json.ShootingResult ?? '',
            StationId: json.StationId,
            Tag: json.Tag ?? null,
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
            TUID: json.TUID ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of UnknownTag from another instance of UnknownTag.
     * @param other The UnknownTag you want to clone
     * @returns A cloned UnknownTag
     */
    public static FromOther(other: UnknownTag): UnknownTag {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return UnknownTag._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return UnknownTag.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('AlphaTag' | 'DeviceId' | 'EventID' | 'ShootingResult' | 'StationId' | 'Tag' | 'Time' | 'TUID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'UnknownTag';
}
